import { OrthographicCamera, Vector2, Vector3 } from 'three';

/** Internal resolution of the visible image, in texels (05 §3). */
export const VIEW_W = 640;
export const VIEW_H = 360;
/** Extra texels rendered on every side so the sub-texel scroll offset never shows an edge. */
export const BORDER = 1;
export const TARGET_W = VIEW_W + BORDER * 2;
export const TARGET_H = VIEW_H + BORDER * 2;

const DISTANCE = 90;

/**
 * Fixed-yaw orthographic camera whose position is snapped to the texel grid.
 *
 * Static geometry therefore always lands on the same pixels; the fraction lost to snapping
 * is handed to the upscale pass as `subTexel`, which slides the image by less than a
 * texel so scrolling still looks smooth (05 §8.2).
 */
export class PixelCamera {
  readonly camera: OrthographicCamera;
  /** World point (on the ground plane) that should sit at the centre of the screen. */
  readonly focus = new Vector3();
  /** Remainder of the snap, in texels, for the upscale pass. Each component is in [-0.5, 0.5]. */
  readonly subTexel = new Vector2();
  /** Integer texel coordinates of the snapped camera: anchors dither patterns to the world. */
  readonly camTexel = new Vector2();
  private ppm: number;
  /** Camera-plane basis for the fixed north-facing view. World: +x east, +y up, +z south. */
  private readonly forward: Vector3;
  private readonly up: Vector3;
  readonly sinPitch: number;
  readonly cosPitch: number;

  /** `pitchDeg` is the angle below the horizontal; 90 would be straight down. */
  constructor(pixelsPerMetre = 16, pitchDeg = 55) {
    const pitch = (pitchDeg * Math.PI) / 180;
    this.sinPitch = Math.sin(pitch);
    this.cosPitch = Math.cos(pitch);
    this.forward = new Vector3(0, -this.sinPitch, -this.cosPitch);
    this.up = new Vector3(0, this.cosPitch, -this.sinPitch);
    this.ppm = pixelsPerMetre;
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 1, 260);
    this.camera.up.set(0, 1, 0);
    this.applyFrustum();
    this.update();
  }

  get pixelsPerMetre(): number {
    return this.ppm;
  }

  set pixelsPerMetre(value: number) {
    this.ppm = value;
    this.applyFrustum();
  }

  /** Metres of ground visible across and up the screen. */
  get viewMetres(): { width: number; depth: number } {
    return { width: VIEW_W / this.ppm, depth: VIEW_H / this.ppm / this.sinPitch };
  }

  private applyFrustum(): void {
    const halfW = TARGET_W / 2 / this.ppm;
    const halfH = TARGET_H / 2 / this.ppm;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.updateProjectionMatrix();
  }

  update(): void {
    const { x: cx, cy } = this.toPlane(this.focus);
    const sx = Math.round(cx * this.ppm) / this.ppm;
    const sy = Math.round(cy * this.ppm) / this.ppm;
    this.subTexel.set((cx - sx) * this.ppm, (cy - sy) * this.ppm);
    this.camTexel.set(Math.round(sx * this.ppm), Math.round(sy * this.ppm));

    const dx = sx - cx;
    const dy = sy - cy;
    const snappedX = this.focus.x + dx;
    const snappedY = this.focus.y + this.up.y * dy;
    const snappedZ = this.focus.z + this.up.z * dy;
    this.camera.position.set(
      snappedX - this.forward.x * DISTANCE,
      snappedY - this.forward.y * DISTANCE,
      snappedZ - this.forward.z * DISTANCE,
    );
    this.camera.lookAt(snappedX, snappedY, snappedZ);
    this.camera.updateMatrixWorld();
  }

  /** Camera-plane coordinates (metres along screen right / screen up) of a world point. */
  toPlane(p: Vector3): { x: number; cy: number } {
    return { x: p.x, cy: p.y * this.up.y + p.z * this.up.z };
  }

  /**
   * Ground point (y = 0) under a position in the visible 640x360 image, in texels from its
   * top-left corner. Inverts the snap and the sub-texel scroll, so it is exact: this is what
   * turns the mouse into an aim point.
   */
  screenToGround(tx: number, ty: number): { x: number; z: number } {
    const planeX = (this.camTexel.x + tx + this.subTexel.x - VIEW_W / 2) / this.ppm;
    const planeY = (this.camTexel.y + (VIEW_H / 2 - ty) + this.subTexel.y) / this.ppm;
    return { x: planeX, z: -planeY / this.sinPitch };
  }

  /**
   * Texel lock (05 §6.2): nudge a world position so it projects onto a whole texel, moving
   * only along the ground (x and z) so feet stay planted. Returns `out`.
   */
  snapToTexel(p: Vector3, out: Vector3): Vector3 {
    const { x: px, cy: py } = this.toPlane(p);
    const dx = Math.round(px * this.ppm) / this.ppm - px;
    const dy = Math.round(py * this.ppm) / this.ppm - py;
    return out.set(p.x + dx, p.y, p.z - dy / this.sinPitch);
  }
}
